import { useLingui } from '@lingui/react/macro';
import { useCallback } from 'react';

import { SportType } from 'modules/core/domain/enums/sport-type';
import { useOrganizer } from 'modules/organizer/ui/hooks/use-organizer';
import { useLazyListTeamsQuery } from 'modules/teams/store/teams-api';
import { UDAsyncSelect } from 'ud-form/components/select/component';
import { TeamOption, TeamSelectOption, teamToTeamOption } from './team-option';

/** Сколько команд показывать в выдаче — остальные пользователь дофильтрует вводом. */
const SEARCH_LIMIT = 10;

const selectComponents = {
  SingleValue: (props: any) => <TeamOption team={props.data?.data} />,
};

interface TeamAsyncSelectProps {
  value?: TeamSelectOption;
  onChange: (option: TeamSelectOption | null) => void;
  onBlur?: () => void;
  name?: string;
  className?: string;
  placeholder?: string;
}

/**
 * Поиск команды текущей организации по названию.
 *
 * Организацию и вид спорта берёт сам — вызывающей стороне остаётся только принять
 * выбранную команду. Используется там, где команду надо к чему-то привязать: в заявках
 * соревнования и в добавлении игрока в команду.
 */
export function TeamAsyncSelect({ value, onChange, onBlur, name, className, placeholder }: TeamAsyncSelectProps) {
  const { t } = useLingui();
  const { info } = useOrganizer();
  const [searchTeams] = useLazyListTeamsQuery();

  const handleLoadOptions = useCallback(
    async (inputValue: string) => {
      if (!info?.id) return [];

      // `true` — preferCacheValue: тот же ввод между открытиями селекта берётся из кэша.
      const teams = await searchTeams(
        {
          tournamentId: info.id,
          limit: SEARCH_LIMIT,
          offset: 0,
          sportType: info.sportType ?? SportType.football,
          namePart: inputValue,
        },
        true,
      ).unwrap();

      return teams.map(teamToTeamOption);
    },
    [info, searchTeams],
  );

  return (
    <UDAsyncSelect<TeamSelectOption>
      className={className}
      name={name}
      placeholder={placeholder ?? t`Введите название`}
      components={selectComponents}
      loadOptions={handleLoadOptions}
      value={value}
      onChange={(option) => onChange(option ?? null)}
      onBlur={onBlur}
    />
  );
}
